const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// The bid-gaps processor emits one payload shape for both of its analyses.
const BID_GAPS_SOURCES = new Set(['bid-gaps', 'bid-leveling']);

const nonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

/**
 * Resolve the display text for a bid-gaps finding.
 *
 * Legacy findings store `CUSTOM_MESSAGE` as a plain string and render through
 * the `FT001` template; the bid-gaps processor instead stores an object
 * (`{ source: 'bid-gaps' | 'bid-leveling', requirement, bid_extract, ... }`)
 * that the template cannot interpolate. For those we surface the `bid_extract`,
 * falling back to the `requirement` when the row has no extract. Returns `null`
 * for every other shape (and when neither field is usable) so callers fall back
 * to the existing string / template path unchanged.
 */
export const getBidGapsCustomMessage = (
  messageContext: Record<string, unknown> | undefined,
): string | null => {
  const customMessage = messageContext?.CUSTOM_MESSAGE;
  if (
    !isRecord(customMessage) ||
    typeof customMessage.source !== 'string' ||
    !BID_GAPS_SOURCES.has(customMessage.source)
  ) {
    return null;
  }

  // The finding context is camelCased on the way in (`bid_extract` -> `bidExtract`);
  // accept the raw snake_case too so this works whichever shape a caller passes.
  return (
    nonEmptyString(customMessage.bidExtract) ??
    nonEmptyString(customMessage.bid_extract) ??
    nonEmptyString(customMessage.requirement)
  );
};
